import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ChannelEnum } from '../../../../../js/api/models/ChannelEnum';
import type { NeedDetail } from '../../../../../js/api/models/NeedDetail';
import type { NeedNote } from '../../../../../js/api/models/NeedNote';
import type { PaginatedNeedNoteList } from '../../../../../js/api/models/PaginatedNeedNoteList';
import type { PatchedNeedNote } from '../../../../../js/api/models/PatchedNeedNote';
import type { PatchedNeedStatusUpdate } from '../../../../../js/api/models/PatchedNeedStatusUpdate';
import type { PatchedNeedUpdate } from '../../../../../js/api/models/PatchedNeedUpdate';
import { UrgencyLevelEnum } from '../../../../../js/api/models/UrgencyLevelEnum';
import { StaffingService } from '../../../../../js/api/services/StaffingService';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { SendItConfirmationDialog } from '../../../components/auto-create-opportunity-dialog';
import { Avatar } from '../../../components/avatar';
import { Button } from '../../../components/button';
import { DropdownSelect } from '../../../components/dropdown-select';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Fieldset, Field, Label, Description } from '../../../components/fieldset';
import StatusPill from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { Textarea } from '../../../components/textarea';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { useStatusTransitions } from '../../../hooks/useStatusTransitions';
import { NEED_NOTES_QUERY_KEY, NEED_QUERY_KEY, STAFFING_NEEDS_LIST_QUERY_KEY } from './queryKeys';

const formSchema = z.object({
  urgency_level: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
  current_status: z.string().nullable().optional(),
  current_reason: z.string().nullable().optional(),
  comment: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const noteFormSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
  channel: z.string().nullable().optional(),
});

type NoteFormData = z.infer<typeof noteFormSchema>;

type NeedDrawerProps = {
  needId: string;
  urgencyLevelMap: Record<string, string>;
  openedFromList?: boolean;
};

type NoteItemProps = {
  note: NeedNote;
  needId: string;
};

function NoteItem({ note, needId }: NoteItemProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const editNoteForm = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      comment: note.comment || '',
      channel: note.channel || null,
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async (data: PatchedNeedNote) => {
      return await StaffingService.staffingNeedsNotePartialUpdate({
        id: note.id,
        requestBody: data,
      });
    },
    onSuccess: () => {
      toast.success('Note updated successfully');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: [NEED_NOTES_QUERY_KEY, needId] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating note' });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async () => {
      return await StaffingService.staffingNeedsNoteDestroy({
        id: note.id,
      });
    },
    onSuccess: () => {
      toast.success('Note deleted successfully');
      queryClient.invalidateQueries({ queryKey: [NEED_NOTES_QUERY_KEY, needId] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error deleting note' });
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
    editNoteForm.reset({
      comment: note.comment || '',
      channel: note.channel || null,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    editNoteForm.reset({
      comment: note.comment || '',
      channel: note.channel || null,
    });
  };

  const handleSave = editNoteForm.handleSubmit(async (data) => {
    await updateNoteMutation.mutateAsync({
      comment: data.comment,
      channel: (data.channel as ChannelEnum) || undefined,
    });
  });

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNoteMutation.mutate();
    }
  };

  const initials = note.created_by_name
    ? note.created_by_name
        .split(' ')
        .map((n) => n[0])
        .filter(Boolean)
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  const createdAt = note.note_at ? new Date(note.note_at) : null;
  const formattedTime = createdAt ? format(createdAt, 'EE, MM/dd/yyyy hh:mm a') : '';

  const updatedAt = note.updated_at ? new Date(note.updated_at) : null;
  const formattedUpdatedTime = updatedAt ? format(updatedAt, 'EE, MM/dd/yyyy hh:mm a') : '';

  // TODO: use code def for translation
  const channelLabel =
    note.channel === 'HCO'
      ? 'Healthcare Organization'
      : note.channel === 'SENDIT'
        ? 'Sendit'
        : note.channel || '';

  return (
    <div className="tw:flex tw:gap-3">
      <Avatar
        initials={initials}
        className="tw:size-8 tw:shrink-0 tw:text-white tw:bg-zinc-600 tw:dark:bg-zinc-500"
      />
      <div className="tw:flex-1 tw:min-w-0">
        <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
          <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:mb-1 tw:mr-3">
            <span className="tw:text-sm tw:font-medium tw:text-zinc-900 tw:dark:text-zinc-100">
              {note.created_by_name || 'Unknown'}
            </span>
            <span className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
              {formattedTime}
            </span>
          </div>
          {note.allow_edit && (
            <div className="tw:flex tw:gap-2 tw:items-center tw:mb-1">
              <button
                type="button"
                onClick={handleEdit}
                className="tw:text-zinc-500 tw:hover:text-zinc-400 tw:dark:text-zinc-400 tw:dark:hover:text-zinc-300 tw:transition-colors"
                aria-label="Edit note"
              >
                <i className="fas fa-edit tw:text-sm" />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteNoteMutation.isPending}
                className="tw:text-stoplight-danger tw:hover:text-stoplight-danger/50 tw:dark:text-zinc-400 tw:dark:hover:text-stoplight-danger tw:transition-colors tw:disabled:opacity-50"
                aria-label="Delete note"
              >
                <i className="far fa-trash-can tw:text-sm" />
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSave} className="tw:space-y-3">
            <div className="tw:bg-zinc-100 tw:dark:bg-zinc-800/50 tw:rounded-lg tw:p-3 tw:mb-1">
              <div className="tw:space-y-3">
                {/* <Field>
                  <Controller
                    name="channel"
                    control={editNoteForm.control}
                    render={({ field }) => (
                      <Select
                        styles={reactSelectReset}
                        placeholder="Select channel..."
                        isClearable
                        options={
                          noteChannelOptions?.map((option) => ({
                            value: option.value,
                            label: option.label,
                          })) || []
                        }
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label:
                                  noteChannelOptions?.find((opt) => opt.value === field.value)
                                    ?.label || field.value,
                              }
                            : null
                        }
                        onChange={(selectedOption) => field.onChange(selectedOption?.value || null)}
                      />
                    )}
                  />
                </Field> */}
                <Textarea
                  {...editNoteForm.register('comment')}
                  placeholder="Enter a comment..."
                  rows={4}
                  disabled={updateNoteMutation.isPending}
                />
                {editNoteForm.formState.errors.comment && (
                  <FieldValidationErrorMessage
                    message={editNoteForm.formState.errors.comment.message}
                  />
                )}
              </div>
            </div>
            <div className="tw:flex tw:justify-end tw:gap-2">
              <Button
                type="button"
                outline
                onClick={handleCancel}
                disabled={updateNoteMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" color="brandgreen0" disabled={updateNoteMutation.isPending}>
                {updateNoteMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="tw:flex tw:flex-col tw:gap-2 tw:bg-zinc-100 tw:dark:bg-zinc-800/50 tw:rounded-lg tw:p-3 tw:mb-1">
              <p className="tw:text-sm tw:text-zinc-900 tw:dark:text-zinc-100  tw:whitespace-pre-line">
                {note.comment}
              </p>
            </div>
            <div className="tw:flex tw:items-center tw:justify-between">
              {note.was_edited && formattedUpdatedTime && (
                <div className="tw:text-xs tw:text-zinc-500 tw:dark:text-zinc-400 tw:text-right">
                  Updated {formattedUpdatedTime}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function NeedDrawer({ needId, urgencyLevelMap, openedFromList = true }: NeedDrawerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: need,
    isLoading: isLoadingNeed,
    error: needError,
  } = useQuery<NeedDetail>({
    queryKey: [NEED_QUERY_KEY, needId],
    queryFn: () => StaffingService.staffingNeedsRetrieve({ id: needId }),
    enabled: !!needId,
  });

  const { data: urgencyLevelOptions } = useCodeDefinitionsAsOptions('UrgencyLevel');
  // const { data: noteChannelOptions } = useCodeDefinitionsAsOptions('HCONoteChannel');

  // Transform API string to form object for urgency_level
  const transformUrgencyLevelApiToForm = useCallback(
    (value: string | null | undefined) => {
      if (!value) return null;
      return urgencyLevelOptions?.find((u) => u.value === value) || { value, label: value };
    },
    [urgencyLevelOptions]
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      urgency_level: transformUrgencyLevelApiToForm(need?.urgency_level),
      current_status: need?.current_status?.code || null,
      current_reason: need?.current_reason?.code || null,
      comment: '',
    },
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  const watchedCurrentStatus = watch('current_status');
  const watchedCurrentReason = watch('current_reason');

  // Track the previous status to detect changes
  const [previousStatus, setPreviousStatus] = useState<string | null>(
    need?.current_status?.code || null
  );

  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<PatchedNeedStatusUpdate | null>(
    null
  );
  const [pendingUrgencyLevelUpdate, setPendingUrgencyLevelUpdate] =
    useState<PatchedNeedUpdate | null>(null);

  // Update form when need data loads
  useEffect(() => {
    if (need) {
      const initialStatus = need.current_status?.code || null;
      reset({
        urgency_level: transformUrgencyLevelApiToForm(need.urgency_level),
        current_status: initialStatus,
        current_reason: need.current_reason?.code || null,
        comment: '',
      });
      setPreviousStatus(initialStatus);
    }
  }, [need, reset, transformUrgencyLevelApiToForm]);

  const { data: notesData, isLoading: isLoadingNotes } = useQuery({
    queryKey: [NEED_NOTES_QUERY_KEY, needId],
    queryFn: () =>
      StaffingService.staffingNeedsNotesList({
        id: needId,
        ordering: '-created_at', // Most recent first
      }),
    enabled: !!needId,
    select: (data: PaginatedNeedNoteList | NeedNote | NeedNote[]) => {
      if (!data) {
        return [];
      }
      // Handle array of notes
      if (Array.isArray(data)) {
        return data;
      }
      // Handle paginated response
      if ('results' in data) {
        return data.results || [];
      }
      // Handle single note
      return [data as NeedNote];
    },
  });

  const notes = useMemo(() => {
    if (!notesData) {
      return [];
    }
    if (Array.isArray(notesData)) {
      return notesData;
    }
    return [];
  }, [notesData]);

  const { availableTransitions, getRequiredFields } = useStatusTransitions({
    currentStatus: need?.current_status?.code || null,
    workflowScope: 'staffing.need',
  });

  const currentStatusRequirements = useMemo(() => {
    if (!watchedCurrentStatus) {
      return { requiresReason: false, requiresNote: false, availableReasons: [] };
    }
    const required = getRequiredFields(watchedCurrentStatus);
    return {
      requiresReason: required.reason,
      requiresNote: required.note,
      availableReasons: required.reasons || [],
    };
  }, [watchedCurrentStatus, getRequiredFields]);

  const statusHasChanged = useMemo(() => {
    // Normalize null/undefined to null for comparison
    const watchedStatus = watchedCurrentStatus ?? null;
    const needStatus = need?.current_status?.code ?? null;
    return watchedStatus !== needStatus;
  }, [watchedCurrentStatus, need?.current_status?.code]);

  const workflowHasChanged = useMemo(() => {
    // Normalize null/undefined to null for comparison
    const watchedReason = watchedCurrentReason ?? null;
    const needReason = need?.current_reason?.code ?? null;
    const reasonHasChanged = watchedReason !== needReason;
    return statusHasChanged || reasonHasChanged;
  }, [statusHasChanged, watchedCurrentReason, need?.current_reason?.code]);

  const commentIsRequired = useMemo(() => {
    return statusHasChanged && currentStatusRequirements.requiresNote;
  }, [statusHasChanged, currentStatusRequirements.requiresNote]);

  const reasonIsRequired = useMemo(() => {
    return statusHasChanged && currentStatusRequirements.requiresReason;
  }, [statusHasChanged, currentStatusRequirements.requiresReason]);

  const statusOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];

    if (need?.current_status) {
      options.push({
        value: need.current_status.code,
        label: need.current_status.title,
      });
    }

    availableTransitions.forEach((status) => {
      options.push({
        value: status.code,
        label: status.title,
      });
    });

    return options;
  }, [need?.current_status, availableTransitions]);

  const reasonOptions = useMemo(() => {
    return currentStatusRequirements.availableReasons.map((reason) => ({
      value: reason.code,
      label: reason.title,
    }));
  }, [currentStatusRequirements.availableReasons]);

  // Clear reason when status changes or if it's not valid for the current status
  useEffect(() => {
    // Update previous status tracker
    if (watchedCurrentStatus !== previousStatus) {
      setPreviousStatus(watchedCurrentStatus ?? null);
    }

    if (!watchedCurrentStatus) {
      // If status is cleared, clear reason too
      if (watchedCurrentReason) {
        setValue('current_reason', null);
      }
      return;
    }

    // If status changed, clear reason to force user to select a new one
    const statusChanged = previousStatus !== null && watchedCurrentStatus !== previousStatus;
    if (statusChanged && watchedCurrentReason) {
      setValue('current_reason', null);
      return;
    }

    // If there's no reason set, check if we need to validate
    if (!watchedCurrentReason) return;

    const availableReasonCodes = currentStatusRequirements.availableReasons.map((r) => r.code);

    // Clear reason if: (1) status has no reasons, OR (2) current reason is not in the available list
    const shouldClearReason =
      availableReasonCodes.length === 0 || !availableReasonCodes.includes(watchedCurrentReason);

    if (shouldClearReason) {
      setValue('current_reason', null);
    }
  }, [
    watchedCurrentStatus,
    watchedCurrentReason,
    previousStatus,
    currentStatusRequirements,
    setValue,
  ]);

  const updateUrgencyLevelMutation = useMutation({
    mutationFn: async (data: PatchedNeedUpdate) => {
      return await StaffingService.staffingNeedsPartialUpdate({
        id: needId,
        requestBody: data,
      });
    },
    onSuccess: () => {
      toast.success('Urgency level updated successfully');
      queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, needId] });
      queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY, 'list'] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating urgency level' });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (data: PatchedNeedStatusUpdate) => {
      return await StaffingService.staffingNeedsStatusUpdatePartialUpdate({
        id: needId,
        requestBody: data,
      });
    },
    onSuccess: () => {
      toast.success('Status updated successfully');
      queryClient.invalidateQueries({ queryKey: [NEED_NOTES_QUERY_KEY, needId] });
      queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, needId] });
      queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY, 'list'] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating status' });
    },
  });

  const noteForm = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      comment: '',
      channel: 'HCO',
    },
  });

  const createNoteMutation = useMutation({
    mutationFn: async (data: NoteFormData) => {
      return await StaffingService.staffingNeedsNotesCreate({
        id: needId,
        requestBody: {
          need: needId,
          comment: data.comment,
          channel: data.channel || undefined,
        } as NeedNote,
      });
    },
    onSuccess: () => {
      toast.success('Note added successfully');
      noteForm.reset();
      queryClient.invalidateQueries({ queryKey: [NEED_NOTES_QUERY_KEY, needId] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error adding note' });
    },
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onSubmit = handleSubmit(async (formData) => {
    try {
      // Validate reason if required
      // Only require a reason if the status has changed AND the new status requires a reason
      if (reasonIsRequired && !formData.current_reason) {
        setError('current_reason', {
          type: 'manual',
          message: 'Reason is required for this status',
        });
        return;
      }

      // Validate comment if required
      // Only require a note if the status has changed AND the new status requires a note
      if (commentIsRequired && !formData.comment?.trim()) {
        setError('comment', {
          type: 'manual',
          message: 'Note is required for this status',
        });
        return;
      }

      // Update urgency level if it changed
      const urgencyLevelValue = formData.urgency_level?.value || null;
      const urgencyLevelUpdate =
        urgencyLevelValue !== need?.urgency_level
          ? {
              urgency_level: urgencyLevelValue as UrgencyLevelEnum,
            }
          : null;

      // Update status/reason if either changed
      const hasComment = formData.comment?.trim();

      if (workflowHasChanged || hasComment) {
        const statusUpdatePayload: PatchedNeedStatusUpdate = {
          code: formData.current_status || undefined,
          reason_code: formData.current_reason || null,
        };

        // Include comment if provided
        if (hasComment && formData.comment) {
          statusUpdatePayload.comment = formData.comment.trim();
        }

        // Check if we need confirmation dialog for SENDIT reason_code
        // Skip confirmation if there are already open opportunities (category_kind !== "C")
        // TODO: need to match on something more than a string "C" here
        const hasOpenOpportunities =
          need?.linked_opportunities?.some((opp) => opp.current_status?.category_kind !== 'C') ??
          false;

        if (statusUpdatePayload.reason_code === 'SENDIT' && !hasOpenOpportunities) {
          setPendingStatusUpdate(statusUpdatePayload);
          setPendingUrgencyLevelUpdate(urgencyLevelUpdate);
          setShowStatusConfirmation(true);
          return; // Exit early, will continue after user confirms/cancels
        }

        // No confirmation needed, proceed with updates
        if (urgencyLevelUpdate) {
          await updateUrgencyLevelMutation.mutateAsync(urgencyLevelUpdate);
        }
        await updateStatusMutation.mutateAsync(statusUpdatePayload);
      } else if (urgencyLevelUpdate) {
        // Only urgency level changed, no status update needed
        await updateUrgencyLevelMutation.mutateAsync(urgencyLevelUpdate);
      }

      if (!workflowHasChanged && !hasComment && urgencyLevelValue === need?.urgency_level) {
        toast.success('No changes to save');
      }
    } catch (error) {}
  });

  const confirmStatusUpdate = async () => {
    if (!pendingStatusUpdate) return;

    try {
      // Proceed with urgency level update if needed
      if (pendingUrgencyLevelUpdate) {
        await updateUrgencyLevelMutation.mutateAsync(pendingUrgencyLevelUpdate);
      }

      // Proceed with status update
      await updateStatusMutation.mutateAsync(pendingStatusUpdate);

      // Close dialog and reset state
      setShowStatusConfirmation(false);
      setPendingStatusUpdate(null);
      setPendingUrgencyLevelUpdate(null);
    } catch (error) {
      // Errors are handled by individual mutation onError handlers
      setShowStatusConfirmation(false);
      setPendingStatusUpdate(null);
      setPendingUrgencyLevelUpdate(null);
    }
  };

  const cancelStatusUpdate = async () => {
    // Skip status update but proceed with urgency level update if needed
    if (pendingUrgencyLevelUpdate) {
      try {
        await updateUrgencyLevelMutation.mutateAsync(pendingUrgencyLevelUpdate);
      } catch (error) {
        // Errors are handled by individual mutation onError handlers
      }
    }

    // Close dialog and reset state
    setShowStatusConfirmation(false);
    setPendingStatusUpdate(null);
    setPendingUrgencyLevelUpdate(null);
  };

  if (isLoadingNeed) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading need details...</div>
      </div>
    );
  }

  if (needError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading need details. Please try again.
        </div>
      </div>
    );
  }

  if (!need) {
    return null;
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      {/* Tabs Section */}
      <Tabs defaultTab={openedFromList ? 'details' : 'comments'}>
        <TabsList>
          {openedFromList && <TabsButton tab="details">Details</TabsButton>}
          <TabsButton tab="comments">Comments</TabsButton>
        </TabsList>
        {openedFromList && (
          <TabsContent tab="details">
            <>
              <div className="tw:flex tw:justify-between tw:items-center tw:mb-4">
                {/* Job Requisition Info */}
                {(need.job_requisition_key || need.job_requisition_url) && (
                  <div>
                    <span className="tw:font-medium">Job Requisition: </span>
                    {need.job_requisition_url ? (
                      <a
                        href={need.job_requisition_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw:text-brand-green-0 tw:hover:underline tw:truncate tw:inline-block tw:max-w-[200px] tw:align-bottom tw:dark:text-brand-green-1"
                        title={need.job_requisition_url}
                      >
                        {need.job_requisition_key || 'View'}
                      </a>
                    ) : (
                      <span>{need.job_requisition_key}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Form Section */}
              <form onSubmit={onSubmit} className="tw:space-y-4">
                <Fieldset>
                  <div className="tw:grid tw:grid-cols-2 tw:md:grid-cols-2 tw:gap-4">
                    <Field>
                      <Label>Urgency Level</Label>
                      <Controller
                        name="urgency_level"
                        control={control}
                        render={({ field }) => (
                          <DropdownSelect
                            allOptions={urgencyLevelOptions}
                            value={field.value}
                            onChange={(selectedOption) => field.onChange(selectedOption || null)}
                            placeholder="Select urgency level..."
                          />
                        )}
                      />
                      {errors.urgency_level && (
                        <FieldValidationErrorMessage message={errors.urgency_level.message} />
                      )}
                    </Field>
                  </div>
                  <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mt-4">
                    <Field>
                      <Label>Status</Label>
                      <Description>
                        {need.current_status
                          ? `Current: ${need.current_status.title}`
                          : 'Select a status'}
                      </Description>
                      <Controller
                        name="current_status"
                        control={control}
                        render={({ field }) => (
                          <DropdownSelect
                            allOptions={statusOptions}
                            value={field.value}
                            onChange={(selectedOption) => {
                              if (!selectedOption || Array.isArray(selectedOption)) {
                                field.onChange(null);
                                return;
                              }
                              field.onChange(selectedOption.value || null);
                            }}
                            placeholder="Select status..."
                          />
                        )}
                      />
                      {errors.current_status && (
                        <FieldValidationErrorMessage message={errors.current_status.message} />
                      )}
                    </Field>

                    {currentStatusRequirements.availableReasons.length > 0 && (
                      <Field>
                        <Label>
                          {watchedCurrentStatus === 'CLOSED' ? 'Reason' : 'Labor Tier'}
                          {currentStatusRequirements.requiresReason && (
                            <span className="tw:text-stoplight-danger">*</span>
                          )}
                        </Label>
                        <Description>
                          {currentStatusRequirements.requiresReason
                            ? 'Required for this status'
                            : 'Optional'}
                        </Description>
                        <Controller
                          name="current_reason"
                          control={control}
                          render={({ field }) => (
                            <DropdownSelect
                              allOptions={reasonOptions}
                              value={field.value}
                              onChange={(selectedOption) => {
                                if (!selectedOption || Array.isArray(selectedOption)) {
                                  field.onChange(null);
                                  return;
                                }
                                field.onChange(selectedOption.value || null);
                              }}
                              placeholder="Select reason..."
                            />
                          )}
                        />
                        {errors.current_reason && (
                          <FieldValidationErrorMessage message={errors.current_reason.message} />
                        )}
                      </Field>
                    )}
                  </div>

                  {/* Note textarea - only shown when status or reason has changed */}
                  {workflowHasChanged && (
                    <div className="tw:mt-4">
                      <Field>
                        <Label>
                          Note
                          {commentIsRequired && <span className="tw:text-stoplight-danger">*</span>}
                        </Label>
                        <Description>
                          {commentIsRequired ? 'Required for this status' : 'Optional'}
                        </Description>
                        <Controller
                          name="comment"
                          control={control}
                          render={({ field }) => (
                            <Textarea
                              {...field}
                              placeholder="Add a note about this status change..."
                              rows={3}
                            />
                          )}
                        />
                        {errors.comment && (
                          <FieldValidationErrorMessage message={errors.comment.message} />
                        )}
                      </Field>
                    </div>
                  )}

                  <div className="tw:mt-6 tw:flex tw:justify-end">
                    <Button
                      type="submit"
                      color="brandgreen0"
                      disabled={
                        isSubmitting ||
                        updateUrgencyLevelMutation.isPending ||
                        updateStatusMutation.isPending
                      }
                    >
                      {isSubmitting ||
                      updateUrgencyLevelMutation.isPending ||
                      updateStatusMutation.isPending
                        ? 'Saving...'
                        : 'Save'}
                    </Button>
                  </div>
                </Fieldset>
              </form>
              <div className="tw:mt-3">
                <div className="tw:font-semibold tw:border-b tw:border-zinc-300 tw:dark:border-zinc-600 tw:text-zinc-900 tw:dark:text-zinc-300 pb-1">
                  Linked Opportunities
                </div>
                <div className="tw:mt-2">
                  {need.linked_opportunities && need.linked_opportunities.length > 0 ? (
                    <div className="tw:space-y-2">
                      {need.linked_opportunities.map((opportunity) => (
                        <div
                          key={opportunity.id}
                          className="tw:flex tw:items-center tw:justify-between tw:py-1 tw:border-b tw:border-zinc-100 last:tw:border-b-0 tw:dark:border-zinc-700"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/app/organization/opportunities/${opportunity.id}/edit`)
                            }
                            className="tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:text-left tw:truncate tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                            title="View opportunity"
                          >
                            {opportunity.code || 'Untitled opportunity'}
                          </button>
                          <span className="tw:ml-2 tw:inline-flex tw:items-center">
                            <StatusPill
                              status={opportunity.current_status?.title || 'Unknown'}
                              category={opportunity.current_status?.category || 'unknown'}
                            />
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
                      No linked opportunities.
                    </div>
                  )}
                </div>
              </div>
            </>
          </TabsContent>
        )}
        <TabsContent tab="comments">
          <div className="tw:space-y-6">
            <form
              onSubmit={noteForm.handleSubmit((data) => createNoteMutation.mutate(data))}
              className="tw:space-y-4"
            >
              <Fieldset>
                <div className="tw:grid tw:grid-cols-1 tw:gap-4">
                  {/* <div className="tw:grid tw:grid-cols-2 tw:gap-4">
                    <Field>
                      <Controller
                        name="channel"
                        control={noteForm.control}
                        render={({ field }) => (
                          <Select
                            styles={reactSelectReset}
                            placeholder="Select channel..."
                            isClearable
                            options={
                              noteChannelOptions?.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })) || []
                            }
                            value={
                              field.value
                                ? {
                                    value: field.value,
                                    label:
                                      noteChannelOptions?.find((opt) => opt.value === field.value)
                                        ?.label || field.value,
                                  }
                                : null
                            }
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value || null)
                            }
                          />
                        )}
                      />
                      {noteForm.formState.errors.channel && (
                        <FieldValidationErrorMessage
                          message={noteForm.formState.errors.channel.message}
                        />
                      )}
                    </Field>
                  </div> */}
                  <Field>
                    <Textarea
                      {...noteForm.register('comment')}
                      placeholder="Enter a comment..."
                      rows={4}
                      disabled={createNoteMutation.isPending}
                    />
                    {noteForm.formState.errors.comment && (
                      <FieldValidationErrorMessage
                        message={noteForm.formState.errors.comment.message}
                      />
                    )}
                  </Field>
                </div>
                <div className="tw:flex tw:justify-end tw:mt-4">
                  <Button type="submit" color="brandgreen0" disabled={createNoteMutation.isPending}>
                    {createNoteMutation.isPending ? 'Adding...' : 'Add Comment'}
                  </Button>
                </div>
              </Fieldset>
            </form>

            {/* Notes List */}
            {isLoadingNotes ? (
              <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading comments...</div>
            ) : notes.length === 0 ? (
              <div className="tw:text-zinc-500 tw:dark:text-zinc-400">No comments yet...</div>
            ) : (
              <div className="tw:space-y-4">
                {notes.map((note) => (
                  <NoteItem key={note.id} note={note} needId={needId} />
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <SendItConfirmationDialog
        open={showStatusConfirmation}
        onClose={cancelStatusUpdate}
        onConfirm={confirmStatusUpdate}
      />
    </div>
  );
}
